import type {
  SecretServiceAddSecretVersionBodyBody,
  SecretServiceUpdateSecretBodyBody,
  V1SecretSchema,
} from '@/generated/hooks/gringotts/models';
import { getOAuthApp } from '@/generated/hooks/gringotts/v1/services/oauth_app_service';
import {
  addSecretVersion,
  addSecretVersion2,
  getSecret2,
  updateSecret,
  updateSecret2,
} from '@/generated/hooks/gringotts/v1/services/secret_service';
import { captureException } from '@/modules/sentry';

import {
  markManagedOAuthReturn,
  unmarkManagedOAuthReturn,
} from './oauth-resume';
import { ECredentialScope, type TCredential } from './types';
import { getCredentialScope, toPayloadFields } from './utils';

// Writes go to the credential's own scope, so they need that scope's permission.
const isOrganizationWide = (credential: TCredential) =>
  getCredentialScope(credential) === ECredentialScope.Organization;

const idOf = (credential: TCredential): string => {
  if (!credential.id) throw new Error('Credential has no id');
  return credential.id;
};

const addVersion = (
  organizationId: string,
  workspaceId: string,
  credential: TCredential,
  body: SecretServiceAddSecretVersionBodyBody,
) =>
  isOrganizationWide(credential)
    ? addSecretVersion(organizationId, idOf(credential), body)
    : addSecretVersion2(organizationId, workspaceId, idOf(credential), body);

const update = (
  organizationId: string,
  workspaceId: string,
  credential: TCredential,
  body: SecretServiceUpdateSecretBodyBody,
) =>
  isOrganizationWide(credential)
    ? updateSecret(organizationId, idOf(credential), body)
    : updateSecret2(organizationId, workspaceId, idOf(credential), body);

/**
 * Replaces a credential's value with a new version. Values are write-only, so
 * this is a full replace: every field comes from `values`.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @param credential - The credential to update.
 * @param values - Field id → value.
 * @param schema - The credential type's schema, used to encode dictionary fields.
 */
export const replaceCredentialValue = async (
  organizationId: string,
  workspaceId: string,
  credential: TCredential,
  values: Record<string, string>,
  schema?: V1SecretSchema,
): Promise<void> => {
  await addVersion(organizationId, workspaceId, credential, {
    payload: { fields: toPayloadFields(values, schema) },
  });
};

/**
 * Renames a credential. Gringotts replaces a credential's labels and expiry
 * with whatever an update sends, so the current ones are read and sent back
 * unchanged; the etag rejects the write if the credential changed meanwhile.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @param credentialId - The credential to rename.
 * @param displayName - The new name.
 */
export const renameCredential = async (
  organizationId: string,
  workspaceId: string,
  credentialId: string,
  displayName: string,
): Promise<void> => {
  const { data: credential } = await getSecret2(
    organizationId,
    workspaceId,
    credentialId,
  );
  await update(organizationId, workspaceId, credential, {
    secret: {
      display_name: displayName,
      labels: credential.labels,
      expires_at: credential.expires_at,
    },
    etag: credential.etag,
  });
};

/**
 * Starts signing an existing OAuth credential in again. Arguments the user
 * filled in at the first sign-in (e.g. a tenant) are kept on the credential as
 * labels and sent again. A failed sign-in leaves the credential as it was.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @param credentialId - The OAuth credential.
 * @returns The provider's consent URL; none when the app signs in without one
 *   (client credentials), in which case the credential is already updated.
 */
export const reauthorizeCredential = async (
  organizationId: string,
  workspaceId: string,
  credentialId: string,
): Promise<{ authorizeUrl?: string }> => {
  const { data: credential } = await getSecret2(
    organizationId,
    workspaceId,
    credentialId,
  );

  let signInArguments: Record<string, string> = {};
  if (credential.oauth_app_id) {
    try {
      const { data: app } = await getOAuthApp(
        organizationId,
        credential.oauth_app_id,
        { workspace_id: workspaceId },
      );
      const labels = credential.labels ?? {};
      signInArguments = Object.fromEntries(
        (app.config?.arguments ?? []).flatMap(argument =>
          argument.id &&
          !argument.constant &&
          !argument.capture &&
          labels[argument.id]
            ? [[argument.id, labels[argument.id]]]
            : [],
        ),
      );
    } catch (error) {
      // Without the app, sign in without arguments; Gringotts reports any
      // that are required.
      captureException(error as Error, {
        tags: { module: 'credentials', action: 'reauthorizeReadApp' },
      });
    }
  }

  // The request captures this page as the return address.
  markManagedOAuthReturn();
  try {
    const { data } = await addVersion(organizationId, workspaceId, credential, {
      oauth_payload: { arguments: signInArguments },
    });
    if (!data.authorize_url) unmarkManagedOAuthReturn();
    return { authorizeUrl: data.authorize_url || undefined };
  } catch (error) {
    unmarkManagedOAuthReturn();
    throw error;
  }
};
